import type { ReferenceType } from '@floating-ui/react';

import {
  FloatingPortal,
  autoUpdate,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
  useTransitionStatus,
} from '@floating-ui/react';
import { type ReactNode, useState } from 'react';

import { usePlayerRef } from '../../contexts/player-ref-context';

import styles from './tooltip.module.css';

type Props = {
  hideTooltip?: boolean;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  tooltip: string;
  trigger: (ref: (node: ReferenceType | null) => void, props: Record<string, unknown>) => ReactNode;
};

export function Tooltip({ placement = 'top', trigger, tooltip, hideTooltip }: Props) {
  const { playerRef } = usePlayerRef();

  const [isOpen, setIsOpen] = useState(false);

  const {
    refs,
    floatingStyles,
    context,
    placement: calculatedPlacement,
  } = useFloating({
    open: isOpen && !hideTooltip,
    onOpenChange: setIsOpen,
    placement,
    // Make sure the tooltip stays on the screen
    whileElementsMounted: autoUpdate,
    middleware: [offset(5), shift()],
  });

  // Event listeners to change the open state
  const hover = useHover(context, {
    move: false,
    delay: {
      open: 500,
      close: 0,
    },
  });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  // Role props for screen readers
  const role = useRole(context, { role: 'tooltip' });

  // Merge all the interactions into prop getters
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  const { isMounted, status } = useTransitionStatus(context);

  return (
    <>
      {trigger(refs.setReference, getReferenceProps({ 'aria-label': tooltip?.toString() }))}
      <FloatingPortal root={playerRef?.current ?? null}>
        {isMounted && (
          <div
            {...getFloatingProps()}
            ref={refs.setFloating}
            style={floatingStyles}
            data-placement={calculatedPlacement}
            data-status={status}
            className={styles.tooltip}
          >
            {tooltip}
          </div>
        )}
      </FloatingPortal>
    </>
  );
}
